import leadsData from '../data/leads.json';
import { Interaction } from '../redux/slices/leadSlice';
import { Product, Campaign } from '../types/marketing';
import { Lead } from '../types/lead';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

// Função para carregar produtos do localStorage ou usar dados iniciais
const loadProducts = (): Product[] => {
  // Verificar se há produtos salvos no localStorage
  const savedProducts = localStorage.getItem('products');
  if (savedProducts) {
    return JSON.parse(savedProducts);
  }
  
  // Retorna um array vazio em vez de criar produtos fictícios
  return [];
};

// Função para salvar produtos no localStorage
const saveProducts = (products: Product[]) => {
  localStorage.setItem('products', JSON.stringify(products));
};

// Função para obter todos os produtos
export const getProducts = async (): Promise<Product[]> => {
  // Simular delay de rede
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Carregar produtos do localStorage ou gerar novos
  const products = loadProducts();
  
  // Forçar o salvamento para garantir que os dados estejam no localStorage
  saveProducts(products);
  
  return products;
};

// Função para adicionar um produto
export const addProduct = async (product: Product): Promise<Product> => {
  try {
    // Verificar se o produto tem campos muito grandes
    const productToSave = { ...product };
    
    // Se a imagem for muito grande, substituir por uma URL de placeholder
    if (productToSave.imageUrl && productToSave.imageUrl.startsWith('data:image')) {
      const base64Size = Math.ceil((productToSave.imageUrl.length * 3) / 4);
      if (base64Size > 500000) { // Se for maior que ~500KB
        productToSave.imageUrl = 'https://via.placeholder.com/300x140?text=Produto';
      }
    }
    
    // Se o vídeo for muito grande, remover
    if (productToSave.videoUrl && productToSave.videoUrl.startsWith('data:video')) {
      const base64Size = Math.ceil((productToSave.videoUrl.length * 3) / 4);
      if (base64Size > 1000000) { // Se for maior que ~1MB
        productToSave.videoUrl = '';
      }
    }
    
    // Tentar salvar o produto
    try {
      const products = loadProducts();
      products.push(productToSave);
      saveProducts(products);
      return productToSave;
    } catch (saveError) {
      console.error('Erro ao salvar produto:', saveError);
      
      // Se falhar, tentar novamente sem a imagem e vídeo
      if (productToSave.imageUrl !== 'https://via.placeholder.com/300x140?text=Produto' || productToSave.videoUrl) {
        const fallbackProduct = {
          ...productToSave,
          imageUrl: 'https://via.placeholder.com/300x140?text=Produto',
          videoUrl: ''
        };
        
        const products = loadProducts();
        products.push(fallbackProduct);
        saveProducts(products);
        return fallbackProduct;
      }
      
      throw saveError;
    }
  } catch (error) {
    console.error('Erro ao adicionar produto:', error);
    throw error;
  }
};

// Função para obter um produto específico
export const getProduct = (id: string): Promise<Product | undefined> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const products = loadProducts();
      const product = products.find(p => p.id === id);
      resolve(product);
    }, 300);
  });
};

// Função para deletar um produto
export const deleteProduct = async (id: string): Promise<string> => {
  try {
    const products = loadProducts();
    const filteredProducts = products.filter(p => p.id !== id);
    saveProducts(filteredProducts);
    return id;
  } catch (error) {
    console.error('Erro ao deletar produto:', error);
    throw error;
  }
};

// Função para atualizar um produto
export const updateProduct = async (product: Product): Promise<Product> => {
  try {
    // Verificar se o produto tem campos muito grandes
    const productToUpdate = { ...product };
    
    // Se a imagem for muito grande, substituir por uma URL de placeholder
    if (productToUpdate.imageUrl && productToUpdate.imageUrl.startsWith('data:image')) {
      const base64Size = Math.ceil((productToUpdate.imageUrl.length * 3) / 4);
      if (base64Size > 500000) { // Se for maior que ~500KB
        productToUpdate.imageUrl = 'https://via.placeholder.com/300x140?text=Produto';
      }
    }
    
    // Se o vídeo for muito grande, remover
    if (productToUpdate.videoUrl && productToUpdate.videoUrl.startsWith('data:video')) {
      const base64Size = Math.ceil((productToUpdate.videoUrl.length * 3) / 4);
      if (base64Size > 1000000) { // Se for maior que ~1MB
        productToUpdate.videoUrl = '';
      }
    }
    
    // Tentar atualizar o produto
    try {
      const products = loadProducts();
      const index = products.findIndex(p => p.id === productToUpdate.id);
      if (index === -1) {
        throw new Error('Produto não encontrado');
      }
      
      // Mantém o código e tipo originais
      const originalProduct = products[index];
      productToUpdate.code = originalProduct.code;
      productToUpdate.type = originalProduct.type;
      
      products[index] = productToUpdate;
      saveProducts(products);
      return productToUpdate;
    } catch (saveError) {
      console.error('Erro ao atualizar produto:', saveError);
      
      // Se falhar, tentar novamente sem a imagem e vídeo
      if (productToUpdate.imageUrl !== 'https://via.placeholder.com/300x140?text=Produto' || productToUpdate.videoUrl) {
        const fallbackProduct = {
          ...productToUpdate,
          imageUrl: 'https://via.placeholder.com/300x140?text=Produto',
          videoUrl: ''
        };
        
        const products = loadProducts();
        const index = products.findIndex(p => p.id === fallbackProduct.id);
        if (index === -1) {
          throw new Error('Produto não encontrado');
        }
        
        // Mantém o código e tipo originais
        const originalProduct = products[index];
        fallbackProduct.code = originalProduct.code;
        fallbackProduct.type = originalProduct.type;
        
        products[index] = fallbackProduct;
        saveProducts(products);
        return fallbackProduct;
      }
      
      throw saveError;
    }
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    throw error;
  }
};

// Função para carregar leads do localStorage ou usar dados iniciais
const loadLeads = (): Lead[] => {
  const savedLeads = localStorage.getItem('leads');
  if (savedLeads) {
    return JSON.parse(savedLeads);
  }
  
  // Retorna um array vazio em vez de criar leads fictícios
  return [];
};

// Função para salvar leads no localStorage
const saveLeads = (leads: Lead[]) => {
  try {
    console.log(`Salvando ${leads.length} leads no localStorage`);
    
    // Verifica se algum lead tem avatar muito grande
    const leadsWithLargeAvatar = leads.filter(lead => lead.avatar && lead.avatar.length > 1000000);
    if (leadsWithLargeAvatar.length > 0) {
      console.warn(`${leadsWithLargeAvatar.length} leads com avatar muito grande. Tamanho médio: ${leadsWithLargeAvatar.reduce((sum, lead) => sum + (lead.avatar?.length || 0), 0) / leadsWithLargeAvatar.length} bytes`);
    }
    
    // Tenta salvar no localStorage
    try {
      localStorage.setItem('leads', JSON.stringify(leads));
      console.log(`${leads.length} leads salvos no localStorage`);
    } catch (storageError) {
      console.error('Erro ao salvar no localStorage (possível tamanho excedido):', storageError);
      
      // Se falhar, tenta salvar sem os avatares
      const leadsWithoutAvatars = leads.map(lead => ({
        ...lead,
        avatar: lead.avatar ? 'AVATAR_REMOVED' : undefined
      }));
      
      localStorage.setItem('leads', JSON.stringify(leadsWithoutAvatars));
      console.log(`${leads.length} leads salvos no localStorage (avatares removidos)`);
      
      throw new Error('Tamanho dos dados excedeu o limite do localStorage. Avatares foram removidos.');
    }
  } catch (error) {
    console.error('Erro ao salvar leads:', error);
    throw new Error('Erro ao salvar leads');
  }
};

// Função para obter todos os leads
export const getLeads = async (): Promise<Lead[]> => {
  // Simular delay de rede
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Carregar leads do localStorage ou gerar novos
  const leads = loadLeads();
  
  // Forçar o salvamento para garantir que os dados estejam no localStorage
  saveLeads(leads);
  
  return leads;
};

// Função para gerar o próximo código sequencial
const generateNextLeadCode = (leads: Lead[]): string => {
  // Encontra o maior número sequencial atual
  const maxSequential = leads.reduce((max, lead) => {
    if (!lead.uniqueCode) return max;
    const match = lead.uniqueCode.match(/LD(\d{2})(\d{2})(\d{4})/);
    if (!match) return max;
    const sequential = parseInt(match[3], 10);
    return Math.max(max, sequential);
  }, 0);

  // Gera o novo código
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const sequence = (maxSequential + 1).toString().padStart(4, '0');
  
  // Formato: LDAAMMNNNN
  // LD: Prefixo fixo
  // AA: Ano atual (2 dígitos)
  // MM: Mês atual (2 dígitos)
  // NNNN: Número sequencial (4 dígitos)
  return `LD${year}${month}${sequence}`;
};

// Função para criar um novo lead
export const createLead = async (lead: Omit<Lead, 'id' | 'uniqueCode'>): Promise<Lead> => {
  try {
    const leads = loadLeads();
    const uniqueCode = generateNextLeadCode(leads);
    
    const newLead: Lead = {
      ...lead,
      id: Math.random().toString(36).substr(2, 9),
      uniqueCode,
      lastContactDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      interactions: lead.interactions || [],
      notes: lead.notes || '',
      phone: lead.phone || '',
    };
    
    leads.push(newLead);
    saveLeads(leads);

    return newLead;
  } catch (error) {
    console.error('Erro ao criar lead:', error);
    throw error;
  }
};

// Função para obter um lead específico
export const getLead = (id: string): Promise<Lead | undefined> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const leads = loadLeads();
      const lead = leads.find(l => l.id === id);
      resolve(lead);
    }, 300);
  });
};

// Função para atualizar um lead
export const updateLead = async (id: string, lead: Partial<Lead>): Promise<Lead> => {
  try {
    console.log(`Atualizando lead com ID: ${id}`);
    console.log('Dados do lead a serem atualizados:', lead);
    
    const leads = loadLeads();
    const index = leads.findIndex(l => l.id === id);
    if (index === -1) {
      console.error(`Lead com ID ${id} não encontrado`);
      throw new Error('Lead não encontrado');
    }
    
    console.log('Avatar atual:', leads[index].avatar ? 'Sim' : 'Não');
    console.log('Novo avatar:', lead.avatar ? 'Sim' : 'Não');
    
    // Cria uma cópia do lead atual
    const currentLead = { ...leads[index] };
    
    // Atualiza apenas os campos fornecidos
    const updatedLead = {
      ...currentLead,
      ...lead,
      lastContactDate: new Date().toISOString(),
      interactions: lead.interactions || currentLead.interactions || [],
      notes: lead.notes || currentLead.notes || '',
      phone: lead.phone || currentLead.phone || '',
      avatar: lead.avatar || currentLead.avatar || '',
      uniqueCode: currentLead.uniqueCode
    };
    
    console.log('Lead atualizado com avatar:', updatedLead.avatar ? 'Sim' : 'Não');
    
    // Atualiza o lead na lista
    leads[index] = updatedLead;
    
    // Salva a lista atualizada no localStorage
    try {
      saveLeads(leads);
    } catch (saveError) {
      console.error('Erro ao salvar lead com avatar:', saveError);
      
      // Se falhar, tenta salvar sem o avatar
      if (lead.avatar) {
        console.log('Tentando salvar sem o avatar...');
        leads[index] = {
          ...updatedLead,
          avatar: 'AVATAR_REMOVED'
        };
        saveLeads(leads);
        
        // Retorna o lead com o avatar removido
        return {
          ...updatedLead,
          avatar: 'AVATAR_REMOVED'
        };
      } else {
        throw saveError;
      }
    }
    
    console.log(`Lead ${updatedLead.name} atualizado com sucesso. Status: ${updatedLead.status}`);

    return updatedLead;
  } catch (error) {
    console.error('Erro ao atualizar lead:', error);
    throw error;
  }
};

// Função para atualizar o status de um lead
export const updateLeadStatus = async (id: string, status: string): Promise<Lead> => {
  try {
    const leads = loadLeads();
    const index = leads.findIndex(l => l.id === id);
    if (index === -1) {
      throw new Error('Lead não encontrado');
    }
    
    const updatedLead = {
      ...leads[index],
      status,
      lastContactDate: new Date().toISOString()
    };
    
    leads[index] = updatedLead;
    saveLeads(leads);
    
    // Retorna o lead atualizado
    return updatedLead;
  } catch (error) {
    console.error('Erro ao atualizar status do lead:', error);
    throw error;
  }
};

// Função para adicionar uma interação a um lead
export const addLeadInteraction = async (leadId: string, interaction: Interaction): Promise<Lead> => {
  try {
    const leads = loadLeads();
    const index = leads.findIndex(l => l.id === leadId);
    if (index === -1) {
      throw new Error('Lead não encontrado');
    }
    
    const lead = leads[index];
    if (!lead) {
      throw new Error('Lead não encontrado');
    }
    
    if (!lead.interactions) {
      lead.interactions = [];
    }
    
    lead.interactions.push(interaction);
    lead.lastContactDate = interaction.date;
    
    saveLeads(leads);
    
    // Retorna o lead atualizado
    return lead;
  } catch (error) {
    console.error('Erro ao adicionar interação:', error);
    throw error;
  }
};

// Função para deletar um lead
export const deleteLead = async (id: string): Promise<string> => {
  try {
    const leads = loadLeads();
    const leadToDelete = leads.find(l => l.id === id);
    const filteredLeads = leads.filter(l => l.id !== id);
    saveLeads(filteredLeads);

    return id;
  } catch (error) {
    console.error('Erro ao deletar lead:', error);
    throw error;
  }
};

// Função para obter estatísticas de leads
export const getLeadStats = (): Promise<{ total: number, converted: number, conversionRate: number }> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const leads = loadLeads();
      const total = leads.length;
      const converted = leads.filter(l => l.status === 'Convertido').length;
      const conversionRate = total > 0 ? (converted / total) * 100 : 0;
      
      resolve({
        total,
        converted,
        conversionRate
      });
    }, 300);
  });
};

// Função para obter estatísticas de produtos
export const getProductStats = (): Promise<{ total: number }> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const products = loadProducts();
      resolve({
        total: products.length
      });
    }, 300);
  });
};

export const getProductsFromMock = async (): Promise<Product[]> => {
  return [
    {
      id: '1',
      code: 'P001',
      type: 'produto',
      name: 'Produto 1',
      description: 'Descrição do produto 1',
      price: 100,
      imageUrl: 'https://via.placeholder.com/150'
    },
    {
      id: '2',
      code: 'P002',
      type: 'produto',
      name: 'Produto 2',
      description: 'Descrição do produto 2',
      price: 200,
      imageUrl: 'https://via.placeholder.com/150'
    }
  ];
};

// Função para deletar todos os leads
export const deleteAllLeads = (): boolean => {
  try {
    localStorage.removeItem('leads');
    return true;
  } catch (error) {
    console.error('Erro ao excluir todos os leads:', error);
    return false;
  }
};

export const deleteAllProducts = (): boolean => {
  try {
    localStorage.removeItem('products');
    return true;
  } catch (error) {
    console.error('Erro ao excluir todos os produtos:', error);
    return false;
  }
};

export const deleteAllData = (): boolean => {
  try {
    // Remove todos os leads
    localStorage.removeItem('leads');
    
    // Remove todos os produtos
    localStorage.removeItem('products');
    
    // Remove outros dados que possam estar armazenados
    localStorage.removeItem('services');
    localStorage.removeItem('clients');
    
    console.log('Todos os dados foram excluídos com sucesso!');
    return true;
  } catch (error) {
    console.error('Erro ao excluir todos os dados:', error);
    return false;
  }
};

// Função para deletar leads específicos por ID
export const deleteLeadsByIds = async (ids: string[]): Promise<string[]> => {
  try {
    const leads = loadLeads();
    const filteredLeads = leads.filter(l => !ids.includes(l.id));
    saveLeads(filteredLeads);
    return ids;
  } catch (error) {
    console.error('Erro ao deletar leads:', error);
    throw new Error('Erro ao deletar leads');
  }
};

// Função para carregar campanhas do localStorage ou usar dados iniciais
const loadCampaigns = (): Campaign[] => {
  try {
    const savedCampaigns = localStorage.getItem('campaigns');
    if (savedCampaigns) {
      const campaigns = JSON.parse(savedCampaigns);
      console.log(`${campaigns.length} campanhas carregadas do localStorage`);
      return campaigns;
    }
    console.log('Usando dados iniciais de campanhas (vazio)');
    return [];
  } catch (error) {
    console.error('Erro ao carregar campanhas:', error);
    return [];
  }
};

// Função para salvar campanhas no localStorage
const saveCampaigns = (campaigns: Campaign[]) => {
  try {
    localStorage.setItem('campaigns', JSON.stringify(campaigns));
    console.log(`${campaigns.length} campanhas salvas no localStorage`);
  } catch (error) {
    console.error('Erro ao salvar campanhas:', error);
    throw new Error('Erro ao salvar campanhas');
  }
};

// Função para obter todas as campanhas
export const getCampaigns = (): Promise<Campaign[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const campaigns = loadCampaigns();
      console.log(`Retornando ${campaigns.length} campanhas para o cliente`);
      resolve(campaigns);
    }, 300);
  });
};

// Função para criar uma nova campanha
export const createCampaign = async (formData: FormData): Promise<Campaign> => {
  try {
    console.log('Criando campanha com os seguintes dados:');
    console.log('Nome:', formData.get('name'));
    console.log('Tipo:', formData.get('type'));
    console.log('Assunto:', formData.get('subject'));
    console.log('Destinatários:', formData.get('recipients'));
    console.log('Status:', formData.get('status'));
    
    const campaigns = loadCampaigns();
    
    // Cria um novo ID para a campanha
    const id = Math.random().toString(36).substr(2, 9);
    
    // Extrai os dados do FormData
    const name = formData.get('name') as string;
    const type = formData.get('type') as 'email' | 'whatsapp';
    const subject = formData.get('subject') as string;
    const content = formData.get('content') as string;
    const recipients = JSON.parse(formData.get('recipients') as string) as string[];
    
    // Verifica se há datas agendadas
    let scheduledDates: string[] = [];
    if (formData.get('scheduledDates')) {
      scheduledDates = JSON.parse(formData.get('scheduledDates') as string) as string[];
    }
    
    // Define o status com base no valor recebido ou usa 'draft' como padrão
    const statusValue = formData.get('status') as string;
    let status: 'draft' | 'scheduled' | 'sent' | 'failed' = 'draft';
    
    if (statusValue === 'sent' || statusValue === 'scheduled' || statusValue === 'failed') {
      status = statusValue;
    }
    
    // Cria a nova campanha
    const now = new Date().toISOString();
    const newCampaign: Campaign = {
      id,
      name,
      type,
      subject,
      content,
      recipients,
      scheduledDates,
      status,
      createdAt: now,
      updatedAt: now,
      metrics: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        failed: 0
      }
    };
    
    // Adiciona a campanha à lista
    campaigns.push(newCampaign);
    
    // Salva as campanhas no localStorage
    saveCampaigns(campaigns);
    
    console.log('Campanha criada com sucesso:', newCampaign);
    return newCampaign;
  } catch (error) {
    console.error('Erro ao criar campanha:', error);
    throw error;
  }
};

// Função para atualizar uma campanha
export const updateCampaign = async (id: string, campaign: Partial<Campaign>): Promise<Campaign> => {
  try {
    const campaigns = loadCampaigns();
    const index = campaigns.findIndex(c => c.id === id);
    if (index === -1) {
      throw new Error('Campanha não encontrada');
    }
    
    const updatedCampaign = {
      ...campaigns[index],
      ...campaign,
      updatedAt: new Date().toISOString()
    };
    
    campaigns[index] = updatedCampaign;
    saveCampaigns(campaigns);
    
    return updatedCampaign;
  } catch (error) {
    console.error('Erro ao atualizar campanha:', error);
    throw error;
  }
};

// Função para deletar uma campanha
export const deleteCampaign = async (id: string): Promise<void> => {
  try {
    const campaigns = loadCampaigns();
    const filteredCampaigns = campaigns.filter(c => c.id !== id);
    saveCampaigns(filteredCampaigns);
  } catch (error) {
    console.error('Erro ao deletar campanha:', error);
    throw error;
  }
};

// Função para enviar uma campanha
export const sendCampaign = async (id: string): Promise<void> => {
  try {
    const campaigns = loadCampaigns();
    const campaign = campaigns.find(c => c.id === id);
    if (!campaign) {
      throw new Error('Campanha não encontrada');
    }
    
    // Atualiza o status da campanha para 'sent'
    campaign.status = 'sent';
    saveCampaigns(campaigns);
    
    // Aqui você pode adicionar a lógica para enviar a campanha
    console.log(`Campanha ${campaign.name} enviada com sucesso`);
  } catch (error) {
    console.error('Erro ao enviar campanha:', error);
    throw error;
  }
};

// Função para obter métricas de uma campanha
export const getCampaignMetrics = async (id: string): Promise<{
  sentCount: number;
  openCount: number;
  clickCount: number;
}> => {
  try {
    const campaigns = loadCampaigns();
    const campaign = campaigns.find(c => c.id === id);
    if (!campaign) {
      throw new Error('Campanha não encontrada');
    }
    
    // Aqui você pode adicionar a lógica para obter as métricas da campanha
    return {
      sentCount: campaign.recipients.length,
      openCount: Math.floor(campaign.recipients.length * 0.7), // Simulação
      clickCount: Math.floor(campaign.recipients.length * 0.3), // Simulação
    };
  } catch (error) {
    console.error('Erro ao obter métricas da campanha:', error);
    throw error;
  }
};

export const createCampaignFromMock = async (formData: FormData): Promise<Campaign> => {
  try {
    console.log('mockData.createCampaignFromMock: Iniciando criação de campanha');
    console.log('mockData.createCampaignFromMock: FormData recebido:', Object.fromEntries(formData.entries()));

    const statusValue = formData.get('status') as string;
    console.log('mockData.createCampaignFromMock: Status recebido:', statusValue);

    // Validar e definir o status
    let status: Campaign['status'] = 'draft';
    if (statusValue === 'sent' || statusValue === 'scheduled' || statusValue === 'failed') {
      status = statusValue as Campaign['status'];
    }
    console.log('mockData.createCampaignFromMock: Status definido:', status);

    // Processar arquivos de mídia
    let mediaUrl: string | undefined;
    let mediaType: string | undefined;
    
    // Verificar se há arquivos de mídia
    const mediaCount = parseInt(formData.get('mediaCount') as string) || 0;
    if (mediaCount > 0) {
      // Para simulação, vamos criar uma URL fake para a mídia
      mediaUrl = `https://example.com/media/${Date.now()}`;
      // Pegar o tipo do primeiro arquivo de mídia
      mediaType = formData.get('mediaType_0') as string;
    }

    const newCampaign: Campaign = {
      id: Date.now().toString(),
      name: formData.get('name') as string,
      type: formData.get('type') as 'email' | 'whatsapp',
      subject: formData.get('subject') as string,
      content: formData.get('content') as string,
      scheduledDates: formData.getAll('scheduledDates').map(date => date.toString()),
      recipients: formData.getAll('recipients').map(recipient => recipient.toString()),
      status,
      mediaUrl,
      mediaType,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metrics: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        failed: 0
      }
    };

    console.log('mockData.createCampaignFromMock: Nova campanha criada:', newCampaign);

    const campaigns = await getCampaignsFromMock();
    campaigns.push(newCampaign);
    await saveCampaignsToMock(campaigns);

    console.log('mockData.createCampaignFromMock: Campanha salva com sucesso');
    return newCampaign;
  } catch (error) {
    console.error('Erro ao criar campanha no mock:', error);
    throw error;
  }
};

// Funções para manipular campanhas no localStorage
const CAMPAIGNS_STORAGE_KEY = 'campaigns';

export const getCampaignsFromMock = async (): Promise<Campaign[]> => {
  try {
    const campaignsJson = localStorage.getItem(CAMPAIGNS_STORAGE_KEY);
    return campaignsJson ? JSON.parse(campaignsJson) : [];
  } catch (error) {
    console.error('Erro ao carregar campanhas do localStorage:', error);
    return [];
  }
};

export const saveCampaignsToMock = async (campaigns: Campaign[]): Promise<void> => {
  try {
    localStorage.setItem(CAMPAIGNS_STORAGE_KEY, JSON.stringify(campaigns));
  } catch (error) {
    console.error('Erro ao salvar campanhas no localStorage:', error);
    throw error;
  }
};

export const updateCampaignFromMock = async (id: string, campaign: Partial<Campaign>): Promise<Campaign> => {
  try {
    console.log('mockData.updateCampaignFromMock: Atualizando campanha:', id);
    console.log('mockData.updateCampaignFromMock: Dados recebidos:', campaign);

    const statusValue = campaign.status as string;
    console.log('mockData.updateCampaignFromMock: Status recebido:', statusValue);

    // Validar e definir o status
    let status: Campaign['status'] = 'draft';
    if (statusValue === 'sent' || statusValue === 'scheduled' || statusValue === 'failed') {
      status = statusValue as Campaign['status'];
    }
    console.log('mockData.updateCampaignFromMock: Status definido:', status);

    const campaigns = await getCampaignsFromMock();
    const campaignIndex = campaigns.findIndex(c => c.id === id);

    if (campaignIndex === -1) {
      throw new Error('Campanha não encontrada');
    }

    // Atualiza a campanha com os novos dados
    const updatedCampaign = {
      ...campaigns[campaignIndex],
      ...campaign,
      updatedAt: new Date().toISOString()
    };

    console.log('mockData.updateCampaignFromMock: Campanha atualizada:', updatedCampaign);

    // Atualiza a lista de campanhas
    campaigns[campaignIndex] = updatedCampaign;
    await saveCampaignsToMock(campaigns);

    return updatedCampaign;
  } catch (error) {
    console.error('Erro ao atualizar campanha no mock:', error);
    throw error;
  }
}; 